/**
 * Per-capability cell. State class + MVB cell treatment (corner
 * tick + brass border) + finding dot + inline dim opacity.
 *
 * Per the handoff: dimming is set INLINE
 * (`style={{ opacity }}`) not via a CSS `:not()` rule -- a
 * descendant-:not() opacity rule silently failed in testing.
 * Keep the explicit inline value.
 */
import type { CapabilityCell as CellType } from "shared/api/types";
import type { CapabilityState } from "twin/capmap/stateMap";

export interface CapabilityCellProps {
  cap: CellType;
  state: CapabilityState;
  selected: boolean;
  lit: boolean;
  dim: boolean;
  isOrigin: boolean;
  onSelect: (id: string, anchor: HTMLDivElement) => void;
}

export function CapabilityCellTile({
  cap,
  state,
  selected,
  lit,
  dim,
  isOrigin,
  onSelect,
}: CapabilityCellProps): JSX.Element {
  const cls = ["capmap-cell", `capmap-s-${state}`];
  if (cap.mvb_flag) cls.push("capmap-mvb");
  if (isOrigin) cls.push("capmap-origin");
  if (state === "down" || state === "degraded") cls.push("capmap-pulse");
  if (state === "at-risk") cls.push("capmap-pulse-edge");
  if (selected) cls.push("capmap-sel");
  if (lit) cls.push("capmap-lit");

  return (
    <div
      data-testid="capmap-cell"
      data-cap={cap.id}
      data-kind={state}
      data-mvb={cap.mvb_flag ? "true" : "false"}
      data-selected={selected ? "true" : "false"}
      className={cls.join(" ")}
      style={{ opacity: dim ? 0.24 : 1 }}
      /* Round 19 H55 -- a clickable cell was a div with no role: no
         keyboard could reach it and no screen reader called it a
         control. */
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(cap.id, e.currentTarget as HTMLDivElement);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        e.stopPropagation();
        onSelect(cap.id, e.currentTarget as HTMLDivElement);
      }}
    >
      {cap.name}
      {cap.open_finding_count > 0 && (
        <span
          className="capmap-fdot"
          data-testid="capmap-cell-finding"
          title={`${cap.open_finding_count} open finding(s)`}
        />
      )}
    </div>
  );
}
